"use client"

import React from "react"
import { X, MapPin, Phone, Mail, Clock, Star, DollarSign, Package } from "lucide-react"
import { Dialog, Flex, Box, Heading, Text, Badge, ScrollArea } from "@radix-ui/themes"

interface Restaurant {
  _id: string
  name: string
  description: string
  category: string
  address: {
    street: string
    city: string
    state: string
    zipCode: string
    coordinates: {
      latitude: number
      longitude: number
    }
  }
  contact?: {
    phone?: string
    email?: string
  }
  phone?: string
  email?: string
  image: string
  rating: {
    average: number
    count: number
  } | number
  isActive: boolean
  deliveryInfo?: {
    fee: number
    minimumOrder: number
    estimatedTime: string
    radius: number
  }
  deliveryTime?: string
  deliveryFee?: number
  minimumOrder?: number
  operatingHours: {
    [key: string]: { open: string; close: string; isOpen: boolean }
  }
  totalOrders: number
  totalRevenue: number
  completionRate: number
  createdAt: string
}

interface RestaurantDetailsModalProps {
  restaurant: Restaurant
  onClose: () => void
}

const RestaurantDetailsModal: React.FC<RestaurantDetailsModalProps> = ({ restaurant, onClose }) => {
  const days = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"]

  return (
    <Dialog.Root open={true} onOpenChange={onClose}>
      <Dialog.Content style={{ maxWidth: 900 }}>
        <Dialog.Title>Restaurant Details</Dialog.Title>
        
        <Dialog.Description size="2" mb="4">
          View detailed information about {restaurant.name}
        </Dialog.Description>

        <ScrollArea style={{ maxHeight: "70vh" }} scrollbars="vertical">
          <Box>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Restaurant Image and Basic Info */}
            <div className="lg:col-span-1">
              <img
                src={restaurant.image || "/placeholder-restaurant.jpg"}
                alt={restaurant.name}
                className="w-full h-48 object-cover rounded-lg mb-4"
              />

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{restaurant.name}</h3>
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${
                      restaurant.isActive ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
                    }`}
                  >
                    {restaurant.isActive ? "Active" : "Inactive"}
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  <Star className="w-4 h-4 text-yellow-400 fill-current" />
                                     <span className="text-sm text-gray-600 dark:text-gray-300">
                     {typeof restaurant.rating === 'object' && (restaurant.rating as any)?.average 
                       ? `${(restaurant.rating as any).average.toFixed(1)} Rating (${(restaurant.rating as any).count || 0} reviews)`
                       : typeof restaurant.rating === 'number' 
                         ? `${restaurant.rating.toFixed(1)} Rating`
                         : '0.0 Rating'
                     }
                   </span>
                </div>

                <p className="text-gray-600 dark:text-gray-300">{restaurant.description}</p>

                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <MapPin className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-600 dark:text-gray-300">{restaurant.address.street}, {restaurant.address.city}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-600 dark:text-gray-300">{restaurant.phone || restaurant.contact?.phone}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-600 dark:text-gray-300">{restaurant.email || restaurant.contact?.email}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Details and Stats */}
            <div className="lg:col-span-2 space-y-6">
              {/* Delivery Information */}
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-3">Delivery Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-gray-400" />
                    <div>
                      <div className="text-sm font-medium text-gray-900 dark:text-white">{restaurant.deliveryTime || restaurant.deliveryInfo?.estimatedTime}</div>
                      <div className="text-xs text-gray-500">Delivery Time</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-gray-400" />
                    <div>
                      <div className="text-sm font-medium text-gray-900 dark:text-white">SDG {restaurant.deliveryFee || restaurant.deliveryInfo?.fee}</div>
                      <div className="text-xs text-gray-500">Delivery Fee</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Package className="w-4 h-4 text-gray-400" />
                    <div>
                      <div className="text-sm font-medium text-gray-900 dark:text-white">SDG {restaurant.minimumOrder || restaurant.deliveryInfo?.minimumOrder}
                      </div>
                      <div className="text-xs text-gray-500">Minimum Order</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Performance Stats */}
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-3">Performance Statistics</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">{restaurant.totalOrders}</div>
                    <div className="text-sm text-gray-500">Total Orders</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">SDG {restaurant.totalRevenue}</div>
                    <div className="text-sm text-gray-500">Total Revenue</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-purple-600">{restaurant.completionRate}%</div>
                    <div className="text-sm text-gray-500">Completion Rate</div>
                  </div>
                </div>
              </div>

              {/* Operating Hours */}
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Operating Hours
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {days.map((day) => (
                    <div key={day} className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300 capitalize">{day}</span>
                      {restaurant.operatingHours[day]?.isOpen ? (
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          {restaurant.operatingHours[day].open} - {restaurant.operatingHours[day].close}
                        </span>
                      ) : (
                        <span className="text-sm text-red-500">Closed</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Additional Information */}
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-3">Additional Information</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Category:</span>
                    <span className="text-gray-900 dark:text-white">{restaurant.category}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Created:</span>
                    <span className="text-gray-900 dark:text-white">
                      {new Date(restaurant.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Restaurant ID:</span>
                    <span className="text-gray-900 dark:text-white font-mono text-xs">{restaurant._id}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Box>
        </ScrollArea>
        
        <Flex gap="3" mt="6" pt="6" justify="end" style={{ borderTop: "1px solid var(--gray-5)" }}>
          <Dialog.Close>
            <button className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white rounded-md hover:bg-gray-300 dark:hover:bg-gray-600">
              Close
            </button>
          </Dialog.Close>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  )
}

export default RestaurantDetailsModal
